import { Search } from '@mui/icons-material';
import { IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Libraries, useJsApiLoader } from '@react-google-maps/api';
import { latlngType } from '../features/predictionMap/types';
import postSearchWord from '../features/predictionMap/api/postSearchLog';
import { store } from '../store';
import { userModelType } from '../features/auth/types';
import { useSnackbar } from '../components/hooks/useSnackbar';
import { sendAnalyticsEvent } from '../utils/googleAnalytics';

const styles = {
  textField: {
    width: '100%',
    height: 30,
    padding: '0 16px',
  },
};

type Props = {
  placeholder?: string;
  onSearch?: boolean;
  defaultAddress?: string;
};

const useAddressInput = ({
  placeholder = '',
  onSearch = false,
  defaultAddress = '',
}: Props = {}) => {
  const [latlng, setLatLng] = useState<latlngType>(null);
  const [searchAddress, setSearchAddress] = useState<string>('');

  return {
    AddressInput: (
      <AddressInput
        setLatLng={setLatLng}
        placeholder={placeholder}
        onSearch={onSearch}
        setSearchAddress={setSearchAddress}
        defaultAddress={defaultAddress}
      />
    ),
    latlng,
    searchAddress,
  };
};

export default useAddressInput;

type AddressInputProps = {
  setLatLng: React.Dispatch<React.SetStateAction<latlngType | null>>;
  placeholder: string;
  onSearch: boolean;
  setSearchAddress: React.Dispatch<React.SetStateAction<string>>;
  defaultAddress?: string;
};

const libraries = ['geometry'] as Libraries;

const AddressInput = ({
  setLatLng,
  placeholder,
  onSearch,
  setSearchAddress,
  defaultAddress = '',
}: AddressInputProps) => {
  const { t } = useTranslation();
  const [address, setAddress] = React.useState<string>('');
  const { CustomSnackbar, setWindowAlert } = useSnackbar();
  useEffect(() => {
    setAddress(defaultAddress);
  }, [defaultAddress]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyB6Z4EHap_Vkm_yyC0vLUg--eEB8E87S_s',
    libraries: libraries,
  });

  const handleGeocode = async (source: 'button' | 'enter' = 'button') => {
    if (!isLoaded) return;
    // GA4: 地点検索の実行を計測（検索語そのものは送信しない）
    sendAnalyticsEvent('location_search', {
      action: 'submit',
      source,
      query_length: address.length,
      search_log_enabled: onSearch,
    });
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && !!results) {
        const { geometry } = results[0];
        let lat = geometry.location.lat();
        let lng = geometry.location.lng();

        const bdLat = lat.toString().split('.');
        const bdLng = lng.toString().split('.');

        if (bdLat[1].length > 10) {
          lat = Number(`${bdLat[0]}.${bdLat[1].substring(0, 10)}`);
        }

        if (bdLng[1].length > 10) {
          lng = Number(`${bdLng[0]}.${bdLng[1].substring(0, 10)}`);
        }
        setLatLng([lat, lng]);
        // GA4: 地点検索の成功を計測
        sendAnalyticsEvent('location_search', {
          action: 'success',
          source,
          result_count: results.length,
          search_log_enabled: onSearch,
        });
      } else {
        // GA4: 地点検索の失敗を計測
        sendAnalyticsEvent('location_search', {
          action: 'failure',
          source,
          status,
          search_log_enabled: onSearch,
        });
        setWindowAlert(t('pages.editLocations.alert.geocode'), 'error');
      }
    });
    setSearchAddress(address);
    if (onSearch) {
      const user = (await store.getItem('user')) as userModelType;
      const client_id = user.client.id;
      await postSearchWord({ client_id: client_id, word: address });
    }
  };

  const handleEnter = (e: any) => {
    if (e.keyCode === 13) {
      handleGeocode('enter');
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" id="useAddressInput">
        {CustomSnackbar}
        <TextField
          value={address}
          sx={{
            width: '320px',
            '& .MuiInputBase-root': {
              backgroundColor: '#fff',
            },
          }}
          inputProps={{
            style: styles.textField,
          }}
          InputProps={{
            // 入力フィールドの先頭にアイコンを追加
            endAdornment: (
              <InputAdornment position="end">
                <IconButton disabled={false} onClick={() => handleGeocode('button')}>
                  <Search />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={e => setAddress(e.target.value)}
          onKeyDown={handleEnter}
          placeholder={
            (!!placeholder && placeholder) || `${t('common.address')}/${t('common.facility')}`
          }
        />
      </Stack>
    </>
  );
};
